import { Product } from '@/adapters/catalogAdapterInterface';
import { Cart, CartItem } from '@/services/integration/clients/types';
import { Carrier } from '@/services/integration/enums';

/**
 * Reads a cart saved by OrderService/CartBuilder back into what the
 * sign-up and confirm order pages need, so a previous order can be
 * edited and completed.
 */

export type LoadedCartItemType = 'plan' | 'hardware' | 'voice' | 'activationFee' | 'ndc';

export type LoadedCartItem = {
  type: LoadedCartItemType,
  name: string,
  symbillId: string | null
};

export type LoadedCartCustomer = {
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  companyName: string,
  abnNumber: string,
  activationDate: string,
  birthdate: string,
  licenseId: string
};

export type LoadedCart = {
  id: string,
  carrier: Carrier | null,
  locationId: string | null,
  crmId: string | null,
  promoCode?: string,
  contractTerm: number | null,
  items: LoadedCartItem[],
  customer: LoadedCartCustomer,
  // The cart's latest OTP entry is verified (PhoneVerificationStatus from getCart()).
  phoneVerified: boolean
};

export type CatalogProducts = {
  plans: Product[],
  hardware: Product[],
  voice: Product[],
  fees: Product[]
};

const productTypes: LoadedCartItemType[] = ['plan', 'hardware', 'voice', 'activationFee', 'ndc'];

const characteristic = (item: CartItem, name: string): string | null => {
  const value = item.product?.characteristic?.find(char => char.name === name)?.value;
  return value === undefined || value === null ? null : String(value);
};

export const parseCart = (id: string, cart: Cart): LoadedCart => {
  const relatedParty = (role: string): string | null => cart.relatedParty?.find(party => party.role === role)?.name ?? null;
  const contactMedium = (type: string) => cart.contactMedium?.find(medium => medium.mediumType === type)?.mediumCharacteristic;

  const items: LoadedCartItem[] = [];
  let promoCode: string | undefined;
  let contractTerm: number | null = null;

  for (const item of cart.cartItem ?? []) {
    const type = characteristic(item, 'type');

    if (type === 'promotion') {
      // CartBuilder::addPromotion stores the promo code as the serial number.
      promoCode = promoCode ?? (item.product?.productSerialNumber || undefined);
      continue;
    }

    if (!type || !productTypes.includes(type as LoadedCartItemType)) {
      continue;
    }

    if (type === 'plan') {
      // The plan carries the contract length as its term. getCart() returns
      // it as itemTerm, while carts we build use cartTerm.
      const term = item.itemTerm?.[0] ?? item.cartTerm?.[0] ?? item.product?.productTerm?.[0];
      contractTerm = term?.duration.quantity ?? null;
    }

    items.push({
      type: type as LoadedCartItemType,
      name: item.product?.name ?? item.itemPrice?.[0]?.name ?? '',
      symbillId: characteristic(item, 'symbillId')
    });
  }

  return {
    id,
    carrier: relatedParty('Dealer') as Carrier | null,
    locationId: relatedParty('Location ID'),
    crmId: relatedParty('CrmId'),
    promoCode,
    contractTerm,
    items,
    phoneVerified: contactMedium('PhoneVerificationStatus')?.socialNetworkId === 'verified',
    customer: {
      // getCart() returns the name as a single 'Customer' contact medium.
      firstName: contactMedium('Firstname')?.firstname ?? contactMedium('Customer')?.firstname ?? '',
      lastName: contactMedium('Lastname')?.lastname ?? contactMedium('Customer')?.lastname ?? '',
      email: contactMedium('Email')?.emailAddress ?? '',
      phoneNumber: contactMedium('Phone')?.phoneNumber ?? '',
      companyName: contactMedium('BusinessName')?.socialNetworkId ?? '',
      abnNumber: contactMedium('Abn')?.socialNetworkId ?? '',
      activationDate: relatedParty('ActivationDate') ?? '',
      birthdate: relatedParty('Birthdate') ?? '',
      licenseId: relatedParty('License Id') ?? ''
    }
  };
};

/**
 * Compare phone numbers ignoring spaces and +61/0 prefixes.
 */
export const isSamePhone = (a: string | number | null | undefined, b: string | number | null | undefined): boolean => {
  const normalise = (phone: string | number | null | undefined): string => {
    const digits = String(phone ?? '').replace(/\D/g, '');
    return digits.startsWith('61') ? `0${digits.slice(2)}` : digits;
  };
  return normalise(a) !== '' && normalise(a) === normalise(b);
};

/**
 * Find the catalog products for the items in a loaded cart. Items that are no
 * longer in the catalog for this address are skipped.
 */
export const matchCartProducts = (loadedCart: LoadedCart, catalog: CatalogProducts): Product[] => {
  const candidatesFor = (type: LoadedCartItemType): Product[] => {
    switch (type) {
      case 'plan': return catalog.plans;
      case 'hardware': return catalog.hardware;
      case 'voice': return catalog.voice;
      case 'activationFee': return catalog.fees.filter(fee => fee.code !== 'NDC');
      case 'ndc': return catalog.fees.filter(fee => fee.code === 'NDC');
    }
  };

  const matched: Product[] = [];
  for (const item of loadedCart.items) {
    const candidates = candidatesFor(item.type);
    const sameSymbill = (product: Product) => item.symbillId !== null && product.symbillId !== null && String(product.symbillId) === item.symbillId;

    const product =
      candidates.find(product => product.name === item.name && sameSymbill(product)) ??
      candidates.find(product => product.name === item.name) ??
      // Contracts can share a symbill id, so fall back on the contract term before symbill alone.
      (item.type === 'activationFee' ? candidates.find(product => product.term === loadedCart.contractTerm) : undefined) ??
      candidates.find(sameSymbill);

    if (product && !matched.includes(product)) {
      matched.push(product);
    }
  }

  return matched;
};
